const angular = require('angular');
const { debounce, throttle, take } = require('lodash');

const key = {
    up: 38, down: 40, enter: 13, esc: 27,
};

class AutocompleteController {
    constructor($scope, $element, $timeout, moveFocus, announcerService) {
        this.$scope = $scope;
        this.$element = $element;
        this.$timeout = $timeout;
        this.moveFocus = moveFocus;
        this.announcerService = announcerService;
    }

    $onInit() {
        this.text = this.text ? this.text : '';
        if (this.selectedItem) {
            this.selectItem(this.selectedItem);
        } else {
            this.selectedItem = null;
        }
        this.placeholder = this.placeholder ? this.placeholder : '';
        this.minLength = this.minLength ? this.minLength : 1;
        this.debounce = this.debounce ? this.debounce : 250;
        this.isThrottled = this.throttle !== undefined;
        this.isDisabled = this.isDisabled !== undefined ? this.isDisabled : false;
        this.isDropdownOpen = this.isDropdownOpen !== undefined ? this.isDropdownOpen : false;
        this.requireMatch = this.requireMatch !== undefined ? this.requireMatch : false;
        this.requireMatchText = this.requireMatchText ? this.requireMatchText : 'Please select an item from the dropdown';
        this.selectOnFocus = this.selectOnFocus !== undefined ? this.selectOnFocus : false;
        this.showSearchButton = this.showSearchButton !== undefined ? this.showSearchButton : false;
        this.showClearButton = this.showClearButton !== undefined ? this.showClear : true;
        this.isRequired = this.isRequired !== undefined ? this.isRequired : 'This field is required';
        this.showNotFoundMessage = this.showNotFoundMessage !== undefined ? this.showNotFoundMessage : true;

        this.isSearching = false;
        this.selectedItemIndex = null;
        this.itemResults = [];

        this.rateLimitedSearchBoxChanged = this.isThrottled
            ? throttle(this.searchBoxChangedExecute, this.throttle)
            : debounce(this.searchBoxChangedExecute, this.debounce);

        this.isParentFulfilmentSelector = this.parentName === 'fulfilmentSelector';
    }

    // Gets the autocomplete items based on this.text
    // Checks length if defined, takes a maximum if defined
    getAutocompleteItems() {
        // Ensure that the text length > minimum length
        if (angular.isFunction(this.items) && this.text && this.text.trim().length >= this.minLength) {
            this.items().then((result) => {
                // Take only top X results if defined
                this.itemResults = this.maxItems ? take(result, this.maxItems) : result;
                this.announcerService.announce(`${this.itemResults.length} results found`);
                this.isSearching = false;
                this.updateScroll();
            });
        } else {
            this.itemResults = [];
            this.announcerService.announce('No results found');
            this.isSearching = false;
        }
    }

    // ng-change will only trigger if the input is changed via the input box
    searchBoxChanged() {
        this.isSearching = true;
        this.isDropdownOpen = true;
        this.selectedItem = null;
        this.selectedItemIndex = null;

        this.rateLimitedSearchBoxChanged();
    }

    // Executes the search (searchBoxChanged only sets debounce timer)
    searchBoxChangedExecute() {
        this.getAutocompleteItems();

        if (angular.isFunction(this.textChanged)) {
            this.textChanged();
        }
    }

    // When an item is clicked
    itemOnClicked(selectedItem) {
        this.selectItem(selectedItem);
        this.closeDropdown();
        if (angular.isFunction(this.runOnSelect)) {
            // Workaround for double-binding (wait for a tick)
            this.$timeout(() => this.runOnSelect());
        }
    }

    // When the user presses enter (only care if the user selected an item)
    searchBoxOnEnter() {
        if (this.selectedItemIndex != null) {
            this.selectItem(this.itemResults[this.selectedItemIndex]);

            if (angular.isFunction(this.runOnSelect)) {
                // Workaround for double-binding (wait for a tick)
                this.$timeout(() => this.runOnSelect());
            }
        } else if (angular.isFunction(this.runOnEnter) && this.text) {
            // Workaround for double-binding (wait for a tick)
            this.$timeout(() => this.runOnEnter());
        }
        this.closeDropdown();
    }

    // Runs when an item is selected, update the text as well
    selectItem(selectedItem) {
        this.selectedItem = selectedItem;
        this.changeSearchText(selectedItem);
    }

    // When the clear button is pressed
    clearSearch() {
        this.text = '';
        this.selectedItem = null;
        this.itemResults = [];
        this.moveFocus(`#${this.inputId ? this.inputId : ''}`);
    }

    // When an item is hovered over, select it
    onItemHover(index) {
        this.selectedItemIndex = index;
    }

    // Handles Enter/Esc/UpArrow/DownArrow key presses
    onKeyDown($event) {
        switch ($event.keyCode) {
            // Enter
            case key.enter:
                $event.stopPropagation();
                $event.preventDefault();
                this.searchBoxOnEnter();
                break;
            // Escape
            case key.esc:
                $event.stopPropagation();
                $event.preventDefault();
                this.closeDropdown();
                break;
            // Up Arrow
            case key.up:
                $event.stopPropagation();
                $event.preventDefault();
                this.dropdownItemsOnArrow(true);
                this.updateScroll();
                break;
            // Down Arrow
            case key.down:
                $event.stopPropagation();
                $event.preventDefault();
                this.dropdownItemsOnArrow(false);
                this.updateScroll();
                break;
            // Others
            default:
                break;
        }
    }

    // Handles up and down arrow key presses
    dropdownItemsOnArrow(isUpKey) {
        if (this.isDropdownOpen && this.itemResults.length > 0) {
            if (isUpKey) {
                this.selectedItemIndex = this.selectedItemIndex !== null ? Math.max(0, this.selectedItemIndex - 1) : Math.max(0, this.itemResults.length - 1);
            } else {
                this.selectedItemIndex = this.selectedItemIndex !== null ? Math.min(this.itemResults.length - 1, this.selectedItemIndex + 1) : 0;
            }
        } else {
            this.showDropdown();
        }
    }

    // Changes the search text based on the itemText() function, or if not specified, treat as text
    changeSearchText(item) {
        if (this.itemText && angular.isFunction(this.itemText())) {
            this.text = this.itemText()(item);
        } else {
            // No custom item text expression given, assume it's text
            this.text = item;
        }
    }

    // On Focus / On Click
    showDropdown() {
        if (!this.isParentFulfilmentSelector) {
            this.isDropdownOpen = true;
        }
    }

    // On select item / press enter / lost focus / press esc
    closeDropdown() {
        this.isDropdownOpen = false;
        this.isDisabled = false;
        if (angular.isFunction(this.runOnClose)) {
            this.runOnClose();
        }
    }

    // Scroll to the correct position when up/down arrows are pressed
    updateScroll() {
        const listContainerElement = this.$element.find('ul').first();
        const contentsElement = listContainerElement.find('li');

        if (contentsElement.length === 0) {
            return;
        }

        const containerHeight = listContainerElement.height();
        const contentHeight = contentsElement.height();
        const topOfContent = contentHeight * this.selectedItemIndex;
        const bottomOfContent = topOfContent + contentHeight;
        const currentScrollPosition = listContainerElement.scrollTop();

        if (bottomOfContent > currentScrollPosition + containerHeight) {
            listContainerElement.scrollTop(bottomOfContent - containerHeight);
        } else if (topOfContent < currentScrollPosition) {
            listContainerElement.scrollTop(topOfContent);
        }
    }
}

AutocompleteController.$inject = ['$scope', '$element', '$timeout', 'MoveFocus', 'AnnouncerService'];

const autocompleteComponentDefinition = {
    controller: AutocompleteController,
    bindings: {
        inputId: '@', // Unique Id for the autocomplete (string), used for properly focusing
        inputName: '@', // Name for the autocomplete (string), for validation (should not be dynamic)
        // Current Text
        parentName: '@?', // Name for the component who uses autocomplete component. e.g. fulfilment selector, signup, etc
        text: '=?', // Current text in the textbox (string)
        selectedItem: '=?', // Currently selected item (object)
        itemText: '&', // Expression for the text inside the autocomplete box (Func<string>)
        placeholder: '@', // Placeholder text (string)
        label: '@', // Label text (string), fallsback to placeholder if not available
        // Items & Item Fetching Options
        items: '&', // Promise returning a list (Func(List<any>[])), assumes List<string> but can be customized
        maxItems: '<?', // Maximum number of items to show in the autocomplete (int, infinite)
        minLength: '<?', // Minimum length of text before the autocomplete tries to get items (int, 1)
        debounce: '<?', // Amount of time in ms to wait for input (int, 250)
        throttle: '<?', // Amount of time in ms to throttle autocompletions (int, 250). Supersedes debounce.
        showNotFoundMessage: '<?', // Whether or not to show the Not Found message if there are no items (bool, true)
        // 'Events'
        runOnSelect: '&?', // Expression that runs when an item is selected (Func)
        runOnEnter: '&?', // Expression that runs when the user presses enter (unless an item is selected in which case runOnSelect runs) (Func)
        textChange: '&?', // Expression that runs when text is changed (Func)
        runOnClose: '&?', // Expression that runs when the dropdown is closed (Func)
        // Status of the dropdown
        forceDisabled: '<?', // External disable the autocomplete, this will not be modified by the autocomplete (bool)
        isDisabled: '=?', // Is the autocomplete disabled (bool, false)
        isDropdownOpen: '=?', // Used for external features that need to know if the dropdown is currently open or not
        // Validation
        isRequired: '<?', // Whether or not this field is required or not (bool)
        requireMatch: '=?', // Requires a match on a selected item (bool) // TODO
        requireMatchText: '@', // Validation message when requireMatch flags that the text is not a match // TODO
        validationMinLength: '@', // Minimum length for validation (number) // TODO
        validationMaxLength: '@', // Maxumum length for validation (number) // TODO
        // Cosmetics
        selectOnFocus: '<?', // Whether or not to select the text when clicking on the autocomplete (bool, false)
        showSearchButton: '<', // Whether or not to shown the search button (bool, false)
        showClearButton: '<', // Whether or not to show the clear button (bool, true)
        searchBoxClass: '@', // Class to apply to the search box
        dropdownWrapperClass: '@', // Class to apply to the div before the dropdown menu wrapper (for sizing of the box)
        dropdownClass: '@', // Class to apply to the dropdown menu wrapper
        itemClass: '@', // Class to apply to each individual item
        itemSelectClass: '@', // Class to apply when an item is selected through the keyboard
        actionButtonWrapperClass: '@', // Class to apply for the action buttons container
        actionButtonClass: '@', // Class to apply in general for an action button
        clearButtonClass: '@', // Class to apply for the clear button
        searchButtonClass: '@', // Class to apply for the search button
        showDismissButtonFulfilmentSelector: '&', // Expression that runs when input is in focus (for fulfilment selector only)
    },
    transclude: {
        itemSlot: 'autocompleteItem',
        dropDownSuffixSlot: '?autocompleteSuffix',
        notFoundSlot: '?autocompleteNotFound',
    },
    template: require('./autocomplete.component.html').trim(),
};

module.exports = {
    AutocompleteController,
    autocompleteComponentDefinition,
};



// WEBPACK FOOTER //
// ./src/olycommon/autocomplete/autocomplete.component.js